'use client';

import React, {useRef, useState, useEffect} from 'react';

interface LazySectionProps {
    children: React.ReactNode;
    /** Root margin for IntersectionObserver; loads content before it's visible */
    rootMargin?: string;
    /** Placeholder to show before section is visible */
    placeholder?: React.ReactNode;
}

/**
 * LazySection defers rendering of below-fold sections until they approach
 * the viewport. In development mode, all sections render immediately to
 * facilitate local dev and visual verification.
 */
export const LazySection: React.FC<LazySectionProps> = ({
    children,
    rootMargin = '200px',
    placeholder = null,
}) => {
    // CRITICAL: Default to visible in dev/test mode so sections always render locally
    const isNonProd = process.env.NODE_ENV !== 'production';
    const [isVisible, setIsVisible] = useState(isNonProd);
    const ref = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (isNonProd || isVisible) return;
        const el = ref.current;
        if (!el) return;

        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setIsVisible(true);
                    observer.disconnect();
                }
            },
            {rootMargin},
        );

        observer.observe(el);
        return () => observer.disconnect();
    }, [isNonProd, isVisible, rootMargin]);

    if (isVisible) {
        return <>{children}</>;
    }

    return <div ref={ref}>{placeholder}</div>;
};
